/* eslint-disable max-lines, @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */
import { Reducer } from 'redux';

import { TThunkAction } from '.';
import { addFailedAction } from './failedAction';
import { ISubscriptionSchema } from '../repositories/subscriptions/entities/entities/SubscriptionSchema';
import { IGetSubscriptionsResponseSchema } from '../repositories/subscriptions/entities/schemas/GetSubscriptionsResponseSchema';
import {
  IDeleteSubscriptionResponse200,
  IDeleteSubscriptionResponse400,
  fetchDeleteSubscription,
  TDeleteSubscriptionResponse,
} from '../repositories/subscriptions/v1/delete-subscription';
import {
  IEditSubscriptionResponse200,
  IEditSubscriptionResponse400,
  TEditSubscriptionResponse,
  fetchEditSubscription,
} from '../repositories/subscriptions/v1/edit-subscription';
import {
  fetchGetSubscriptionsList,
  TGetSubscriptionsListResponse,
} from '../repositories/subscriptions/v1/get-subscriptions-list';
import { trackFrequencyChange, trackingPageType } from '../tracking';
import { isResponseContainsKey } from '../utils/api';
import { getToggleLabel, serializeDeviceType } from '../utils/tracking';

export const LIMIT_SUBSCRIPTIONS = 25;

export enum ESubscriptionsStatus {
  Initial,
  Loading,
  NextPageLoading,
  Succeed,
  Failed,
}

export interface ISubscriptionsState {
  status: ESubscriptionsStatus;
  data: IGetSubscriptionsResponseSchema | null;
  titleSubscribedOn: string;
}

export interface ISubscriptionsStatusChanged {
  type: 'ISubscriptionsStatusChanged';
  status: ESubscriptionsStatus;
  data?: IGetSubscriptionsResponseSchema | null;
  replace?: boolean;
}

export interface ISubscriptionsDeletedParams {
  categoryName: string;
  itemId: number;
}

export interface ISubscriptionsDeleted extends ISubscriptionsDeletedParams {
  type: 'ISubscriptionsDeleted';
}

export interface ISetTitleSubscribedOnParams {
  title: string;
}

export interface ISetTitleSubscribedOn extends ISetTitleSubscribedOnParams {
  type: 'ISetTitleSubscribedOn';
}

export interface ISubscriptionsFailDeleted extends ISubscriptionsDeletedParams {
  type: 'ISubscriptionsFailDeleted';
  subscription: ISubscriptionSchema[];
}

export interface ISubscriptionTotalDecrement {
  type: 'ISubscriptionTotalDecrement';
}

export function subscriptionsTotalDecrement(): ISubscriptionTotalDecrement {
  return {
    type: 'ISubscriptionTotalDecrement',
  };
}

export interface IFrequencyParams {
  [key: string]: string | number;
  categoryName: string;
  itemId: number;
  frequency: string;
  prevFrequency: string;
}

export interface IFrequencyUpdated extends IFrequencyParams {
  type: 'IFrequencyUpdated';
}

export interface IFrequencyUpdateFailed extends IFrequencyParams {
  type: 'IFrequencyUpdateFailed';
}

export interface ISubscriptionIsActiveParams {
  categoryName: string;
  itemId?: number;
  prevIsActive: boolean;
}

export interface ISubscriptionIsActiveActionParams extends ISubscriptionIsActiveParams {
  isActive: boolean;
}

export interface IIsActiveUpdated extends ISubscriptionIsActiveActionParams {
  type: 'IIsActiveUpdated';
}

export interface IIsActiveUpdateFailed extends ISubscriptionIsActiveParams {
  type: 'IIsActiveUpdateFailed';
}

export function updateIsActive(params: ISubscriptionIsActiveParams): TThunkAction {
  return (dispatch, getState, context) => {
    const { token } = getState();
    const { prevIsActive, ...rest } = params;

    const apiParams = token
      ? Object.assign({}, rest, { token, isActive: !prevIsActive })
      : Object.assign({}, rest, { isActive: !prevIsActive });

    /** Обновляем UI сразу */
    dispatch<IIsActiveUpdated>({
      type: 'IIsActiveUpdated',
      ...params,
      isActive: !prevIsActive,
    });

    /** Если апи зафейлилось - откатываем состояние UI до предыдущего */
    return fetchEditSubscription({ httpApi: context.httpApi, parameters: apiParams })
      .then((resp: TEditSubscriptionResponse) => {
        if (isResponseContainsKey<IEditSubscriptionResponse400, IEditSubscriptionResponse200>(resp, 'errors')) {
          dispatch<ISubscriptionsStatusChanged>({
            status: ESubscriptionsStatus.Failed,
            type: 'ISubscriptionsStatusChanged',
          });

          dispatch(
            addFailedAction({
              type: 'IIsActiveUpdated',
              ...params,
            }),
          );

          dispatch<IIsActiveUpdateFailed>({
            type: 'IIsActiveUpdateFailed',
            ...params,
          });
        }
      })
      .catch(() => {
        dispatch<ISubscriptionsStatusChanged>({
          status: ESubscriptionsStatus.Failed,
          type: 'ISubscriptionsStatusChanged',
        });

        dispatch(
          addFailedAction({
            type: 'IIsActiveUpdated',
            ...params,
          }),
        );

        dispatch<IIsActiveUpdateFailed>({
          type: 'IIsActiveUpdateFailed',
          ...params,
        });
      });
  };
}

export function updateSubscriptionFrequency(params: IFrequencyParams): TThunkAction {
  return (dispatch, getState, context) => {
    const { token, deviceType } = getState();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { prevFrequency, ...rest } = params;
    const { categoryName, frequency } = rest;

    const page = {
      pageType: trackingPageType,
      siteType: serializeDeviceType(deviceType),
    };

    const trackingParams = {
      label: getToggleLabel({ categoryName, frequency }),
      page,
    };

    const apiParams = token ? Object.assign({}, rest, { token }) : Object.assign({}, rest);

    /** Обновляем UI сразу */
    dispatch<IFrequencyUpdated>({
      type: 'IFrequencyUpdated',
      ...params,
    });

    trackFrequencyChange(trackingParams);

    /** Если апи зафейлилось - откатываем состояние UI до предыдущего */
    return fetchEditSubscription({ httpApi: context.httpApi, parameters: apiParams }).catch(() => {
      dispatch<ISubscriptionsStatusChanged>({
        status: ESubscriptionsStatus.Failed,
        type: 'ISubscriptionsStatusChanged',
      });

      dispatch(
        addFailedAction({
          type: 'IFrequencyUpdated',
          ...params,
        }),
      );

      dispatch<IFrequencyUpdateFailed>({
        type: 'IFrequencyUpdateFailed',
        ...params,
      });
    });
  };
}

export function setTitleSubscribedOn(title: string): ISetTitleSubscribedOn {
  return {
    title,
    type: 'ISetTitleSubscribedOn',
  };
}

export function deleteSubscriptionItem(params: ISubscriptionsDeletedParams): TThunkAction {
  return (dispatch, getState, context) => {
    const state = getState();
    const { token, subscriptions } = state;

    const removedSubscription = subscriptions.data
      ? (subscriptions.data.subscriptions || []).filter(subscription => subscription.id === params.itemId)
      : [];

    dispatch<ISubscriptionsDeleted>({
      type: 'ISubscriptionsDeleted',
      ...params,
    });

    if (token) {
      Object.assign(params, { token });
    }

    return fetchDeleteSubscription({ httpApi: context.httpApi, parameters: params })
      .then((resp: TDeleteSubscriptionResponse) => {
        if (isResponseContainsKey<IDeleteSubscriptionResponse400, IDeleteSubscriptionResponse200>(resp, 'errors')) {
          context.logger.error(resp as any);

          /**
           * Добавляем зафейленный экшен
           */
          dispatch(
            addFailedAction({
              type: 'ISubscriptionsDeleted',
              ...params,
            }),
          );

          dispatch<ISubscriptionsFailDeleted>({
            type: 'ISubscriptionsFailDeleted',
            ...params,
            subscription: removedSubscription,
          });
        } else {
          dispatch(subscriptionsTotalDecrement());
        }
      })
      .catch(error => {
        context.logger.error(error);

        /**
         * Добавляем зафейленный экшены
         */
        dispatch(
          addFailedAction({
            type: 'ISubscriptionsDeleted',
            ...params,
          }),
        );

        dispatch<ISubscriptionsFailDeleted>({
          type: 'ISubscriptionsFailDeleted',
          ...params,
          subscription: removedSubscription,
        });
      });
  };
}

export interface IGetSubscriptionsListParameters {
  categoryName: string;
  itemId?: number;
  replace?: boolean;
  offset?: number;
  limit?: number;
}

export function getSubscriptionsList(parameters: IGetSubscriptionsListParameters): TThunkAction {
  const { offset = 0, limit = LIMIT_SUBSCRIPTIONS, categoryName, itemId, replace } = parameters;

  return (dispatch, getState, context) => {
    dispatch<ISubscriptionsStatusChanged>({
      status: replace ? ESubscriptionsStatus.Loading : ESubscriptionsStatus.NextPageLoading,
      type: 'ISubscriptionsStatusChanged',
    });

    const state = getState();
    const { token } = state;

    const apiParameters = {
      categoryName,
      itemId,
      limit,
      offset,
    };

    if (token) {
      Object.assign(apiParameters, { token });
    }

    return fetchGetSubscriptionsList({ httpApi: context.httpApi, parameters: apiParameters })
      .then((response: TGetSubscriptionsListResponse) => {
        if (response.statusCode === 200) {
          dispatch<ISubscriptionsStatusChanged>({
            data: response.response,
            replace,
            status: ESubscriptionsStatus.Succeed,
            type: 'ISubscriptionsStatusChanged',
          });

          return;
        }

        dispatch<ISubscriptionsStatusChanged>({
          status: ESubscriptionsStatus.Failed,
          type: 'ISubscriptionsStatusChanged',
        });
      })
      .catch(error => {
        context.logger.error(error);

        dispatch<ISubscriptionsStatusChanged>({
          status: ESubscriptionsStatus.Failed,
          type: 'ISubscriptionsStatusChanged',
        });
      });
  };
}

const initialState = {
  data: null,
  status: ESubscriptionsStatus.Initial,
  titleSubscribedOn: '',
} as ISubscriptionsState;

export type TSubscriptionsActions =
  | ISubscriptionsStatusChanged
  | ISubscriptionsDeleted
  | ISubscriptionsFailDeleted
  | ISubscriptionTotalDecrement
  | IFrequencyUpdated
  | IFrequencyUpdateFailed
  | IIsActiveUpdated
  | IIsActiveUpdateFailed
  | ISetTitleSubscribedOn;

export const subscriptionsReducer: Reducer<ISubscriptionsState, TSubscriptionsActions> = (
  state = initialState,
  action,
) => {
  switch (action.type) {
    case 'ISubscriptionsStatusChanged':
      return {
        ...state,
        data: action.data
          ? action.replace
            ? action.data
            : {
                ...action.data,
                subscriptions: state.data
                  ? (state.data.subscriptions || []).concat(action.data.subscriptions || [])
                  : action.data.subscriptions,
              }
          : state.data,
        status: action.status,
      };

    case 'ISubscriptionsDeleted':
      /**
       * Если во время удаления подписки сменилась категория - не обновляем UI
       */
      if (state.data && state.data.categoryName !== action.categoryName) {
        return state;
      }

      return {
        ...state,
        data: {
          ...(state.data as IGetSubscriptionsResponseSchema),
          subscriptions: state.data
            ? (state.data.subscriptions || []).filter(subscription => subscription.id !== action.itemId)
            : [],
        },
      };

    case 'ISubscriptionsFailDeleted':
      /**
       * Если во время фейла удаления подписки сменилась категория - не обновляем UI
       */
      if (state.data && state.data.categoryName !== action.categoryName) {
        return state;
      }

      return {
        ...state,
        data: {
          ...(state.data as IGetSubscriptionsResponseSchema),
          subscriptions: state.data ? (state.data.subscriptions || []).concat(action.subscription) : [],
        },
      };

    case 'ISubscriptionTotalDecrement':
      return {
        ...state,
        data: {
          ...(state.data as IGetSubscriptionsResponseSchema),
          total: state.data && state.data.total && state.data.total - 1 > 0 ? state.data.total - 1 : 0,
        },
      };
    case 'IFrequencyUpdated':
      return {
        ...state,
        data: {
          ...(state.data as IGetSubscriptionsResponseSchema),
          subscriptions: getUpdatedFrequencySubscriptions(state.data?.subscriptions || [], action),
        },
      };

    case 'IFrequencyUpdateFailed':
      return {
        ...state,
        data: {
          ...(state.data as IGetSubscriptionsResponseSchema),
          subscriptions: getPrevFrequencySubscriptions(state.data?.subscriptions || [], action),
        },
      };

    case 'IIsActiveUpdated':
      return {
        ...state,
        data: {
          ...(state.data as IGetSubscriptionsResponseSchema),
          subscriptions: getUpdatedActiveSubscriptions(state.data?.subscriptions || [], action),
        },
      };

    case 'IIsActiveUpdateFailed':
      return {
        ...state,
        data: {
          ...(state.data as IGetSubscriptionsResponseSchema),
          subscriptions: getPrevIsActiveSubscriptions(state.data?.subscriptions || [], action),
        },
      };

    case 'ISetTitleSubscribedOn':
      return {
        ...state,
        titleSubscribedOn: action.title,
      };

    default:
      return state;
  }
};

function getUpdatedFrequencySubscriptions(subscriptions: ISubscriptionSchema[], params: IFrequencyParams) {
  return subscriptions.map(subscription =>
    subscription.id === params.itemId ? { ...subscription, frequency: params.frequency } : subscription,
  );
}

function getPrevFrequencySubscriptions(subscriptions: ISubscriptionSchema[], params: IFrequencyUpdateFailed) {
  return subscriptions.map(subscription =>
    subscription.id === params.itemId ? { ...subscription, frequency: params.prevFrequency } : subscription,
  );
}

function getUpdatedActiveSubscriptions(
  subscriptions: ISubscriptionSchema[],
  params: ISubscriptionIsActiveActionParams,
) {
  return subscriptions.map(subscription =>
    subscription.id === params.itemId ? { ...subscription, isActive: params.isActive } : subscription,
  );
}

function getPrevIsActiveSubscriptions(subscriptions: ISubscriptionSchema[], params: ISubscriptionIsActiveParams) {
  return subscriptions.map(subscription =>
    subscription.id === params.itemId ? { ...subscription, isActive: params.prevIsActive } : subscription,
  );
}
